import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import { AvailableRooms, AvailableRoomType, Room } from '../../../domain/types/availableRooms'
import { APIAvailableRooms, APIAvailableRoomType, APIRoom } from '../types/APIAvailableRooms'

const GA4_ITEM_CATEGORY = 'Ancillary > groups > selectRoom'
const GA4_ITEM_CATEGORY2 = 'selectRoom'
const GA4_ITEM_LIST_NAME = 'checkout1'

export interface AvailableRoomsResponseMapper {
  map: (response: APIAvailableRooms) => AvailableRooms
  mapGA4ImpressionItems: (response: APIAvailableRooms) => ImpressionViewAncillariesItem[]
}

export function createAvailableRoomsResponseMapper(): AvailableRoomsResponseMapper {
  function map({ emptyRoomTypes, roomsWithName }: APIAvailableRooms): AvailableRooms {
    return {
      rooms: mapNamedRooms(roomsWithName),
      roomTypes: mapRoomTypes(emptyRoomTypes),
      ga4Impressions: mapGA4ImpressionItems({ emptyRoomTypes, roomsWithName }),
    }
  }

  function mapNamedRooms(roomsWithName: APIRoom[]): Record<string, Room> | null {
    if (!roomsWithName || roomsWithName.length === 0) {
      return null
    }

    return roomsWithName.reduce((acc, room) => {
      return {
        ...acc,
        [room.roomId]: {
          roomId: room.roomId,
          name: room.roomName,
          description: room.roomTypeDescription,
          isFull: room.occupancy >= room.maxOccupancy,
          availableBedsAmount: Math.max(0, room.maxOccupancy - room.occupancy),
          totalBedsAmount: room.maxOccupancy,
          roomType: room.roomType,
        },
      }
    }, {} as Record<string, Room>)
  }

  function mapRoomTypes(roomTypes: APIAvailableRoomType[] | null): Record<string, AvailableRoomType> | null {
    if (!roomTypes || roomTypes.length === 0) {
      return null
    }
    return roomTypes.reduce((acc, emptyRoom) => {
      return {
        ...acc,
        [emptyRoom.roomType]: {
          type: emptyRoom.roomType,
          description: emptyRoom.roomTypeDescription,
          availableRoomsAmount: emptyRoom.amountRooms,
          totalBedsAmount: emptyRoom.maxOccupancy,
        },
      }
    }, {} as Record<string, AvailableRoomType>)
  }

  function mapGA4ImpressionItems({ emptyRoomTypes, roomsWithName }: APIAvailableRooms): ImpressionViewAncillariesItem[] {
    const fromRoomTypes: ImpressionViewAncillariesItem[] = (emptyRoomTypes ?? []).map(roomType => ({
      item_name: roomType.roomTypeDescription,
      item_id: roomType.roomType,
      price: 0,
      item_category: GA4_ITEM_CATEGORY,
      item_category2: GA4_ITEM_CATEGORY2,
      item_list_name: GA4_ITEM_LIST_NAME,
      quantity: '1',
    }))

    const seenRoomTypes = new Set(emptyRoomTypes?.map(r => r.roomType) ?? [])
    const fromNamedRooms: ImpressionViewAncillariesItem[] = (roomsWithName ?? []).reduce<ImpressionViewAncillariesItem[]>((acc, room) => {
      if (seenRoomTypes.has(room.roomType))
        return acc
      seenRoomTypes.add(room.roomType)
      return [
        ...acc,
        {
          item_name: room.roomTypeDescription,
          item_id: room.roomType,
          price: 0,
          item_category: GA4_ITEM_CATEGORY,
          item_category2: GA4_ITEM_CATEGORY2,
          item_list_name: GA4_ITEM_LIST_NAME,
          quantity: '1',
        },
      ]
    }, [])

    return [...fromRoomTypes, ...fromNamedRooms]
  }

  return {
    map,
    mapGA4ImpressionItems,
  }
}
